import clsx from "clsx";
import { Check, ChevronDown, Copy, Pencil, RefreshCw, RotateCcw, Sparkles } from "lucide-react";
import {
  createContext,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { BriefingEnemySheet, BriefingSectionId } from "../../api/types";
import { designate, STALE_REGENERATE } from "../../components/attention";
import { Badge, Button, IconButton, Kbd, KbdHint, shortcut } from "../../components/ui";
import { useUi, type ViewKey } from "../../state/ui";
import { readLocalCopy, useUnsavedDraft } from "../../state/unsavedDrafts";
import { EXIT_MS, motion, usePresence } from "../../components/presence";
import { t, tServer } from "../../i18n/i18n";
import type { DocumentJob } from "./documentJob";
import { useDeployment } from "../../map/units/useDeployment";
import { mentionMatcher, unitMentions, useUnitPeek } from "../order/UnitPeek";
import {
  type BlockItem,
  fieldLabelLength,
  type FloatItem,
  FRAME_HEIGHT,
  FRAME_WIDTH,
  listItems,
  PAGE,
  type Paged,
  pageBreaks,
  paginate,
  sliceWords,
  splitAtLastBreak,
  stack,
  type TextItem,
  textLines,
  wordSpans,
} from "./paginate";
import { Waiting } from "../../components/Waiting";
import { Spinner } from "../../components/EmptyState";

/** Mise en ambiance en feuilles A4 successives, comme l'OPO (demande d'Alexis du 02/10/2026) : quand le texte
 *  dépasse une page, une deuxième feuille A4 s'ajoute sous la première, jamais une longue feuille unique. Chaque
 *  feuille porte le cadre du PDF (bandeau « CONFIDENTIEL EXERCICE », thème tactique, « GENEREX », « Page n/N ») ;
 *  le texte, à la typographie du PDF mise à l'échelle de la feuille, coule d'une feuille à la suivante.
 *
 *  Le contenu reste interactif (survol, modification, régénération, copie, texte qui s'écrit en direct) : les
 *  morceaux du document sont posés dans un même calque, au-dessus des feuilles, aux positions calculées par
 *  `paginate` ; un morceau qui change de page garde son identité (l'éditeur en cours n'est jamais remonté). Les
 *  paragraphes sont mesurés dans un conteneur caché à la largeur du cadre de texte ; les autres blocs (titres,
 *  éditeur, attente de l'IA, fiche de l'ennemi) sur place. */

type Lang = "fr" | "en";
/** Manière d'une section : celles de la mise en ambiance, et pour la fiche pays `facts` (une ligne « Libellé :
 *  valeur » par entrée, libellé en gras) et `list` (éléments à puce). */
export type SectionVariant = "lead" | "body" | "enemy" | "friendly" | "bia" | "facts" | "list";

/** Section affichée : celle de la mise en ambiance, ou une rubrique de la fiche pays présentée de même. */
export interface PagedSection {
  id: string;
  title: string;
  /** « llm » : rédigée par l'IA locale (pastille, attente de l'IA) ; « template » : texte de Generex. */
  kind: "llm" | "template";
  text: string;
  edited: boolean;
  status: string;
  fallback_reason?: string | null;
  stale: boolean;
  withheld?: boolean;
  model: string;
  generated_at: string | null;
  /** Document lu dans l'autre langue que sa langue de rédaction (docs/13) : traduction de ce paragraphe en cours,
   *  attente animée à la place du texte. */
  translating?: boolean;
}

/** Rédaction en cours, telle que les feuilles la montrent (documentJob.ts). */
type PagedJob = Pick<DocumentJob<unknown>, "running" | "steps" | "live" | "sections">;

/** Contenu du document, dans l'ordre du PDF. Fiche pays : `subtitle` (date sous le titre), `image` (carte centrée,
 *  taille en pixels de l'image, hauteur maximale en points), `float` (carte posée d'un côté du cadre : ce qui la
 *  suit coule à côté d'elle) ; section de chiffres en deux colonnes (`columns`), section dont la carte suit
 *  l'intitulé (`figure` : la liste des commandements coule à côté), section d'en-tête toute entière à côté de la
 *  carte du pays (`pinned`). */
export type DocNode<Id extends string = BriefingSectionId> =
  | { type: "title"; key: string; text: string }
  | { type: "subtitle"; key: string; text: string }
  | { type: "heading"; key: string; text: string; level: "part" | "section" }
  | { type: "section"; id: Id; variant: SectionVariant; columns?: 1 | 2; figure?: DocFigure; pinned?: boolean }
  | { type: "image"; key: string; src: string; alt: string; width: number; height: number; maxHeight: number }
  | ({ type: "float" } & DocFigure)
  | { type: "enemySheet"; sheet: BriefingEnemySheet };

/** Carte posée d'un côté du cadre (fiche pays) : taille en points (celle du PDF), côté, hauteur au plus sur une
 *  feuille étroite (points). */
export interface DocFigure {
  key: string;
  src: string;
  alt: string;
  width: number;
  height: number;
  side: "left" | "right";
  maxHeight: number;
}

// --- typographie du PDF (briefing.py : blocks, to_pdf), en points ---------------------------------------------

const FONT = '"Inter OPO", "Inter", system-ui, sans-serif';
const INK = "#000000";
const GRAY = "rgb(128 128 128)";
const RED = "#ff0000";
const BLUE = "#007aff"; // systemBlue de l'iPad, repris par le PDF
/** Ligne de base dans une ligne d'interlignage 1 (Inter : ascendante 0,969, descendante 0,241 du corps). */
const BASELINE = 0.864;

/** Style d'un paragraphe, en points : corps, interligne, couleur, retrait, espace après ; `hang` : retrait des lignes
 *  suivantes seulement (la première commence au bord). */
interface TextStyle {
  size: number;
  leading: number;
  color: string;
  indent: number;
  after: number;
  hang?: number;
}

/** Paragraphes : corps 10, interligne 1,45, justifiés ; préambule en gris. La fiche de l'ennemi (écran seulement)
 *  garde son retrait et sa couleur. */
const TEXT = {
  body: { size: 10, leading: 14.5, color: INK, indent: 0, after: 7 },
  lead: { size: 10, leading: 14.5, color: GRAY, indent: 0, after: 7 },
  sheet: { size: 9.5, leading: 15, color: "var(--color-ink-soft)", indent: 12, after: 6 },
  /** Fiche pays : ligne « Libellé : valeur » (alignée à gauche) et élément de liste à puce (puce à 3 pt). */
  facts: { size: 10, leading: 14.5, color: INK, indent: 0, after: 2 },
  bullet: { size: 10, leading: 14.5, color: INK, indent: 12, after: 2 },
} as const;
type TextStyleId = keyof typeof TEXT;

/** Titres : interligne 1,25, 12 pt avant, 6 pt après. */
const HEADINGS = {
  title: { size: 20, leading: 25, before: 0, after: 6 },
  part: { size: 11, leading: 13.75, before: 12, after: 6 },
  section: { size: 10.5, leading: 13.125, before: 12, after: 6 },
  bia: { size: 10, leading: 12.5, before: 12, after: 6 },
  /** Intitulé du préambule (écran seulement : le PDF n'en a pas). */
  label: { size: 7.5, leading: 13.125, before: 6, after: 4 },
} as const;

/** Titre d'un niveau, en points. */
interface HeadingStyle {
  size: number;
  leading: number;
  before: number;
  after: number;
}
type HeadingStyles = Record<keyof typeof HEADINGS, HeadingStyle>;

/** Date de la fiche pays sous son titre : corps 11, gris, centrée (paragraphe du PDF). */
const SUBTITLE = { size: 11, leading: 15.95, before: 0, after: 7 } as const;
/** Carte de la fiche pays : un peu d'air avant et après, comme dans le PDF. */
const FIGURE = { before: 4, after: 8 } as const;

/** Fiche pays, comme son PDF (server/generex_api/country_layout.py) : titre et date, écart entre une carte et le texte
 *  à côté d'elle (et entre deux colonnes de chiffres), espace sous une carte, réduction la plus forte d'une carte en
 *  bas de page, retrait des chiffres coupés sur plusieurs lignes et des puces, interligne des intitulés. Sous
 *  `narrow` pixels de feuille, une seule colonne : cartes au-dessus de leur rubrique, chiffres un par ligne. */
const FICHE = {
  title: { size: 20, leading: 24, before: 0, after: 0 },
  date: { size: 11, leading: 14, before: 0, after: 10 },
  gutter: 14,
  below: 8,
  minScale: 0.75,
  hang: 8,
  bulletIndent: 12,
  bulletAt: 2,
  headingLeading: 1.2,
  narrow: 640,
} as const;

/** Typographie d'un niveau de la fiche pays (country_layout.py, `Level`), en points ; `leading` en multiple du
 *  corps. */
export interface DocTypography {
  body: number;
  leading: number;
  after: number;
  item: number;
  heading: number;
  before: number;
  under: number;
}

/** Styles des paragraphes : ceux de la mise en ambiance, ou ceux d'un niveau de la fiche pays. */
function textStyles(typo?: DocTypography): Record<TextStyleId, TextStyle> {
  if (!typo) return TEXT;
  const line = { size: typo.body, leading: typo.body * typo.leading, color: INK };
  return {
    ...TEXT,
    body: { ...line, indent: 0, after: typo.after },
    lead: { ...line, color: GRAY, indent: 0, after: typo.after },
    facts: { ...line, indent: FICHE.hang, hang: FICHE.hang, after: typo.item },
    bullet: { ...line, indent: FICHE.bulletIndent, after: typo.item },
  };
}

/** Titres : ceux de la mise en ambiance, ou les intitulés d'un niveau de la fiche pays. */
function headingStyles(typo?: DocTypography): HeadingStyles {
  if (!typo) return HEADINGS;
  const section = { size: typo.heading, leading: typo.heading * FICHE.headingLeading, before: typo.before, after: typo.under };
  return { ...HEADINGS, title: FICHE.title, section };
}

const px = (n: number) => `${n}px`;

/** Largeur d'un texte d'une ligne dans la police donnée (pixels) : intitulé qui doit tenir à côté d'une carte. */
let measureContext: CanvasRenderingContext2D | null | undefined;
function lineWidth(text: string, font: string): number {
  if (measureContext === undefined) measureContext = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
  if (!measureContext) return 0;
  measureContext.font = font;
  return measureContext.measureText(text).width;
}

/** Style d'un paragraphe du document à l'échelle `u` (pixels par point) ; le même sert à la mesure. Les chiffres et
 *  les puces sont alignés à gauche, le texte justifié. */
function textCss(style: TextStyleId, s: TextStyle, u: number, italic: boolean, justifyLast: boolean): CSSProperties {
  return {
    margin: "0px",
    paddingLeft: px(s.indent * u),
    // Toujours écrit : l'élément de mesure sert à tous les paragraphes.
    textIndent: px(-(s.hang ?? 0) * u),
    fontFamily: FONT,
    fontSize: px(s.size * u),
    lineHeight: px(s.leading * u),
    fontWeight: "400",
    fontStyle: italic ? "italic" : "normal",
    color: s.color,
    textAlign: style === "facts" || style === "bullet" ? "left" : "justify",
    // Fragment coupé en bas de page : sa dernière ligne est justifiée, comme dans le PDF.
    textAlignLast: justifyLast ? "justify" : "auto",
    whiteSpace: "pre-line",
    // Pas de césure ni de crénage : les lignes se coupent comme celles du PDF (ReportLab).
    hyphens: "manual",
    fontKerning: "none",
    letterSpacing: "normal",
    overflowWrap: "break-word",
  };
}

function headingCss(h: HeadingStyle, u: number, color: string): CSSProperties {
  return {
    margin: "0px",
    fontFamily: FONT,
    fontSize: px(h.size * u),
    lineHeight: px(h.leading * u),
    fontWeight: "600",
    color,
    letterSpacing: "normal",
    fontKerning: "none",
  };
}

// Désignations d'unités (« 11(TI)CIECHAR », « 68(FR)RAA ») et GDH (« 070600B OCT 26 ») : jamais coupées en fin
// de ligne, ni d'une page à l'autre.
const UNBREAKABLE = /(\d+\([A-Z]{2}\)[A-Za-z0-9]+|\d{6}[A-Z] [A-Z]{3} \d{2})/;

/** Noms d'unités de la mise en ambiance (10/10/2026) : chacun dans un `<span data-unit>`, que le survol désigne
 *  pour la mini-carte ; sans incidence sur la mise en page (élément en ligne, même texte). */
const MentionContext = createContext<ReturnType<typeof mentionMatcher>>(null);

function withMentions(text: string, matcher: ReturnType<typeof mentionMatcher>, key: string): ReactNode {
  if (!matcher || !text) return text;
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(matcher.re)) {
    const start = m.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    out.push(
      <span key={`${key}-${start}`} data-unit={matcher.index.get(m[1]!)} className="cursor-pointer rounded-[2px] transition-colors duration-(--gx-enter) hover:bg-accent/15">
        {m[1]}
      </span>,
    );
    last = start + m[1]!.length;
  }
  if (!out.length) return text;
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function Rich({ text, bold = 0 }: { text: string; bold?: number }) {
  const matcher = useContext(MentionContext);
  if (bold > 0) {
    // Libellé d'une ligne « Libellé : valeur » de la fiche pays, en gras comme dans le PDF.
    return (
      <>
        <span style={{ fontWeight: 600 }}>{text.slice(0, bold)}</span>
        <Rich text={text.slice(bold)} />
      </>
    );
  }
  const parts = text.split(UNBREAKABLE);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="whitespace-nowrap">
            {withMentions(part, matcher, String(i))}
          </span>
        ) : (
          withMentions(part, matcher, String(i))
        ),
      )}
    </>
  );
}

/** Même contenu que `Rich`, écrit dans l'élément de mesure. */
function fillRich(el: HTMLElement, text: string, bold = 0) {
  el.textContent = "";
  if (bold > 0) {
    const label = document.createElement("span");
    label.style.fontWeight = "600";
    label.textContent = text.slice(0, bold);
    el.appendChild(label);
  }
  text.slice(bold).split(UNBREAKABLE).forEach((part, i) => {
    if (!part) return;
    if (i % 2 === 1) {
      const span = document.createElement("span");
      span.style.whiteSpace = "nowrap";
      span.textContent = part;
      el.appendChild(span);
    } else el.appendChild(document.createTextNode(part));
  });
}

/** Caractères du libellé en gras d'un fragment qui commence au mot `from` (ligne « Libellé : valeur »). */
function boldFrom(entry: { bold?: number; spans: [number, number][] }, from: number): number {
  if (!entry.bold) return 0;
  return Math.max(0, entry.bold - (entry.spans[from]?.[0] ?? 0));
}


// --- éléments du flux --------------------------------------------------------------------------------------------

interface Common {
  /** Section du document (survol partagé entre ses morceaux, sur toutes les pages). */
  section?: string;
  lang?: string;
  /** Morceau de la fiche de l'ennemi : filet à gauche. */
  sheet?: boolean;
  /** Fiche de l'ennemi en train de se refermer. */
  exiting?: boolean;
}
type FlowBlock = BlockItem & Common & { render: () => ReactNode };
/** Carte posée d'un côté du cadre (fiche pays), dessinée à la taille de sa place (réduite en bas de page). */
type FlowFloat = FloatItem & Common & { render: (width: number, height: number) => ReactNode };
type FlowText = TextItem &
  Common & {
    text: string;
    spans: [number, number][];
    style: TextStyleId;
    italic: boolean;
    caret: boolean;
    muted: boolean;
    editable: boolean;
    /** Ligne « Libellé : valeur » : nombre de caractères du libellé, en gras. */
    bold?: number;
    /** Élément de liste : puce devant son premier fragment. */
    bullet?: boolean;
  };
type Entry = FlowBlock | FlowText | FlowFloat;

export function PagedBriefing<Id extends string = BriefingSectionId>({
  lang,
  nodes,
  sections,
  job,
  editing,
  savingId,
  busy,
  banner,
  themeLine,
  copyPrefix,
  onEdit,
  onCancelEdit,
  onSave,
  onRegenerate,
  onReset,
  onCopied,
  view = "briefing",
  typography,
  renderChips,
  canRegenerate,
  regenerateLabel,
  editHint,
  rubrics = false,
}: {
  lang: Lang;
  nodes: DocNode<Id>[];
  sections: Partial<Record<Id, PagedSection>>;
  job: PagedJob | null;
  editing: Id | null;
  savingId: Id | null;
  busy: boolean;
  /** Cadre des pages, dans la langue du document (comme le PDF). */
  banner: string;
  themeLine: string;
  /** Début de la clé de la copie locale d'un paragraphe en cours de modification (suivi de l'identifiant). */
  copyPrefix: string;
  onEdit: (id: Id) => void;
  onCancelEdit: () => void;
  /** Rejetée si l'enregistrement échoue (le paragraphe reste alors ouvert avec sa saisie). */
  onSave: (id: Id, text: string) => Promise<unknown>;
  onRegenerate: (id: Id) => void;
  onReset: (id: Id) => void;
  onCopied: () => void;
  /** Vue du document : « Retour » de la carte ouverte sur une unité citée y ramène. */
  view?: ViewKey;
  /** Typographie de la fiche pays (niveau de son PDF) : corps, interligne et espaces ; elle met aussi en place sa
   *  disposition dense (cartes à côté du texte, chiffres en deux colonnes, une seule colonne sur une feuille
   *  étroite) et ses règles de mise en pages (espaces fusionnés, veuves). */
  typography?: DocTypography;
  /** Pastilles d'une section, à la place de celles de la mise en ambiance. */
  renderChips?: (section: PagedSection, state: { writing: boolean; queued: boolean; busy: boolean }) => ReactNode;
  /** Section que l'IA peut rédiger à nouveau (bouton du survol) ; toutes par défaut. */
  canRegenerate?: (section: PagedSection) => boolean;
  /** Infobulle du bouton de rédaction d'une section, à la place de celle de la mise en ambiance. */
  regenerateLabel?: string;
  /** Aide de l'éditeur d'une section, à la place de « une ligne vide sépare deux paragraphes ». */
  editHint?: (id: Id) => string | undefined;
  /** Sections nommées « rubriques » (fiche pays) dans les infobulles et la saisie retrouvée. */
  rubrics?: boolean;
}) {
  // Largeur de la feuille → échelle (pixels par point).
  const stackRef = useRef<HTMLDivElement>(null);
  // Unités citées : survol → mini-carte ; « Voir sur la carte » → carte centrée, « Retour à la mise en ambiance »
  // ramène ici, au même endroit (10/10/2026).
  const deployment = useDeployment();
  const phase = useUi((s) => s.phase);
  const mentions = useMemo(() => unitMentions(deployment.data?.units, phase), [deployment.data, phase]);
  const matcher = useMemo(() => mentionMatcher(mentions), [mentions]);
  const scroller = () => stackRef.current?.closest<HTMLElement>(".overflow-y-auto") ?? null;
  const peek = useUnitPeek((m) => {
    useUi.getState().showUnitOnMap({ code: m.code, lngLat: m.lngLat }, scroller()?.scrollTop ?? 0, view);
  });
  const unitOver = (e: MouseEvent) => {
    const el = (e.target as Element).closest?.("[data-unit]");
    if (!el) return;
    const mention = mentions[Number(el.getAttribute("data-unit"))];
    if (mention) peek.hover(mention, el.getBoundingClientRect());
  };
  const unitOut = (e: MouseEvent) => {
    if ((e.target as Element).closest?.("[data-unit]")) peek.hover(null, null);
  };
  const orderReturn = useUi((s) => s.orderReturn);
  useEffect(() => {
    if (orderReturn?.view !== view) return;
    const frame = window.requestAnimationFrame(() => {
      scroller()?.scrollTo({ top: orderReturn.scroll });
      useUi.getState().clearOrderReturn();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [orderReturn]);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = stackRef.current;
    if (!el) return;
    setWidth(el.offsetWidth);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setWidth(el.offsetWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const u = width / PAGE.width;
  // Typographie : celle de la mise en ambiance, ou celle du niveau de la fiche pays (son PDF).
  const typoKey = typography ? JSON.stringify(typography) : "";
  const styles = textStyles(typography);
  const headings = headingStyles(typography);
  const fiche = Boolean(typography);
  // Feuille étroite : une seule colonne (cartes au-dessus de leur rubrique, chiffres un par ligne).
  const narrow = fiche && width > 0 && width < FICHE.narrow;

  // Polices chargées (Inter du PDF, à la demande) : les mesures sont refaites.
  const [fontEpoch, setFontEpoch] = useState(0);
  useEffect(() => {
    const fonts = typeof document !== "undefined" ? document.fonts : undefined;
    if (!fonts) return;
    let alive = true;
    const bump = () => alive && setFontEpoch((n) => n + 1);
    void fonts.ready.then(bump);
    fonts.addEventListener?.("loadingdone", bump);
    return () => {
      alive = false;
      fonts.removeEventListener?.("loadingdone", bump);
    };
  }, []);

  // Hauteur des blocs insécables, mesurée sur place (elle ne dépend que de la largeur, pas de la page).
  const [heights, setHeights] = useState<Record<string, number>>({});
  const heightsRef = useRef(heights);
  heightsRef.current = heights;
  const [intrinsic, setIntrinsic] = useState<Record<string, number>>({});
  const intrinsicRef = useRef(intrinsic);
  intrinsicRef.current = intrinsic;
  const blockEls = useRef(new Map<string, HTMLElement>());
  const refCallbacks = useRef(new Map<string, (el: HTMLElement | null) => void>());
  const measureBlocks = () => {
    const current = heightsRef.current;
    let changed = false;
    const next: Record<string, number> = { ...current };
    blockEls.current.forEach((el, key) => {
      // Morceau qui s'efface (fiche refermée) : son animation de sortie le réduit, sa place reste la même.
      if (el.className.includes("gx-exit")) return;
      const h = el.getBoundingClientRect().height;
      if (Math.abs((current[key] ?? -1) - h) > 0.5) {
        next[key] = h;
        changed = true;
      }
    });
    if (changed) setHeights(next);
    // Intitulés (fiche pays) : largeur de leur contenu, titre et pastilles, pour savoir s'ils tiennent dans la
    // colonne à côté d'une carte.
    const known = intrinsicRef.current;
    let widened = false;
    const wide: Record<string, number> = { ...known };
    blockEls.current.forEach((el, key) => {
      const inner = el.querySelector<HTMLElement>("[data-intrinsic]");
      if (!inner) return;
      const w = inner.scrollWidth;
      if (Math.abs((known[key] ?? -1) - w) > 0.5) {
        wide[key] = w;
        widened = true;
      }
    });
    if (widened) setIntrinsic(wide);
  };
  const measureRef = useRef(measureBlocks);
  measureRef.current = measureBlocks;
  const observer = useRef<ResizeObserver | null>(null);
  if (!observer.current && typeof ResizeObserver !== "undefined") {
    observer.current = new ResizeObserver(() => measureRef.current());
  }
  useLayoutEffect(() => {
    const obs = observer.current;
    if (!obs) return;
    blockEls.current.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  useLayoutEffect(() => measureBlocks());
  const blockRef = (key: string) => {
    let cb = refCallbacks.current.get(key);
    if (!cb) {
      cb = (el) => {
        const previous = blockEls.current.get(key);
        if (previous && previous !== el) observer.current?.unobserve(previous);
        if (el) {
          blockEls.current.set(key, el);
          observer.current?.observe(el);
        } else blockEls.current.delete(key);
      };
      refCallbacks.current.set(key, cb);
    }
    return cb;
  };

  // Survol : une section coupée sur deux pages se surligne d'un seul tenant.
  const [hovered, setHovered] = useState<string | null>(null);
  const onOver = (e: MouseEvent) => {
    const el = (e.target as Element).closest?.("[data-section]");
    setHovered(el?.getAttribute("data-section") ?? null);
  };

  // Fiche de l'ennemi (sous la 1.1) : repliée par défaut, elle prend sa place dans le flux quand on l'ouvre.
  const sheetNode = nodes.find((n): n is Extract<DocNode<Id>, { type: "enemySheet" }> => n.type === "enemySheet");
  const [openSheet, setOpenSheet] = useState<string | null>(null);
  const sheetOpen = Boolean(sheetNode && openSheet === sheetNode.sheet.eni);
  const sheetPresence = usePresence(sheetOpen);

  // --- construction du flux ------------------------------------------------------------------------------------
  const entries: Entry[] = [];
  const addBlock = (
    key: string,
    estimate: number,
    space: { before: number; after: number },
    render: () => ReactNode,
    extra: Common & { keepWithNext?: boolean; beside?: boolean; minWidth?: number; pinned?: boolean } = {},
  ) =>
    entries.push({
      kind: "block",
      key,
      height: heights[key] ?? estimate * u,
      before: space.before * u,
      after: space.after * u,
      render,
      ...extra,
    });
  const addText = (
    key: string,
    text: string,
    style: TextStyleId,
    extra: Common & {
      italic?: boolean;
      caret?: boolean;
      live?: boolean;
      muted?: boolean;
      editable?: boolean;
      bold?: number;
      bullet?: boolean;
      pinned?: boolean;
    } = {},
  ) => {
    const spans = wordSpans(text);
    if (!spans.length) return;
    entries.push({
      kind: "text",
      key,
      words: spans.length,
      lineHeight: styles[style].leading * u,
      before: 0,
      after: styles[style].after * u,
      breaks: pageBreaks(text, spans, UNBREAKABLE),
      text,
      spans,
      style,
      italic: false,
      caret: false,
      muted: false,
      editable: false,
      ...extra,
    });
  };
  const paragraphs = (text: string) =>
    text
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean);
  // Carte posée d'un côté du cadre, à la taille du PDF : ce qui la suit coule à côté d'elle, puis dessous ; sur une
  // feuille étroite, centrée au-dessus de ce qui la suit, hauteur bornée.
  const addFigure = (figure: DocFigure) => {
    if (narrow) {
      const ratio = figure.height / Math.max(figure.width, 1);
      const shown = Math.min(figure.maxHeight, FRAME_WIDTH * ratio);
      const figureWidth = (shown / Math.max(ratio, 1e-6)) * u;
      addBlock(`i:${figure.key}:${figure.src}`, shown, FIGURE, () => (
        <Figure src={figure.src} alt={figure.alt} width={figureWidth} height={shown * u} />
      ));
      return;
    }
    entries.push({
      kind: "float",
      key: `f:${figure.key}:${figure.src}`,
      width: figure.width * u,
      height: figure.height * u,
      side: figure.side,
      gap: FICHE.gutter * u,
      below: FICHE.below * u,
      minScale: FICHE.minScale,
      before: 0,
      after: 0,
      render: (w, h) => <Figure src={figure.src} alt={figure.alt} width={w} height={h} />,
    });
  };

  for (const node of nodes) {
    if (node.type === "title") {
      addBlock(`t:${node.key}`, headings.title.leading, headings.title, () => (
        <h2 style={{ ...headingCss(headings.title, u, INK), textAlign: "center", textWrap: "balance" }}>{node.text}</h2>
      ));
    } else if (node.type === "subtitle") {
      const date = fiche ? FICHE.date : SUBTITLE;
      addBlock(`t:${node.key}`, date.leading, date, () => (
        <p
          style={{
            margin: "0px",
            fontFamily: FONT,
            fontSize: px(date.size * u),
            lineHeight: px(date.leading * u),
            fontWeight: "400",
            color: GRAY,
            textAlign: "center",
            letterSpacing: "normal",
            fontKerning: "none",
          }}
        >
          {node.text}
        </p>
      ));
    } else if (node.type === "image") {
      // Carte : pleine largeur du cadre, hauteur bornée (comme dans le PDF) ; une autre carte est un autre bloc.
      const ratio = node.height / Math.max(node.width, 1);
      const shown = Math.min(node.maxHeight, FRAME_WIDTH * ratio);
      const figureWidth = (shown / Math.max(ratio, 1e-6)) * u;
      addBlock(`i:${node.key}:${node.src}`, shown, FIGURE, () => (
        <Figure src={node.src} alt={node.alt} width={figureWidth} height={shown * u} />
      ));
    } else if (node.type === "float") {
      addFigure(node);
    } else if (node.type === "heading") {
      const level = node.level;
      addBlock(
        `t:${node.key}`,
        headings[level].leading,
        headings[level],
        () =>
          level === "part" ? (
            <h3 style={headingCss(headings.part, u, GRAY)}>{node.text}</h3>
          ) : (
            <h4 style={headingCss(headings.section, u, GRAY)}>{node.text}</h4>
          ),
        { keepWithNext: true },
      );
    } else if (node.type === "enemySheet") {
      const sheet = node.sheet;
      const english = sheet.language === "en";
      const sheetLang = english ? "en" : "fr";
      addBlock(
        "es:toggle",
        24 / Math.max(u, 0.01),
        { before: 4, after: 4 },
        () => (
          <button
            type="button"
            onClick={() => setOpenSheet((o) => (o === sheet.eni ? null : sheet.eni))}
            aria-expanded={sheetOpen}
            className="-ml-2 inline-flex min-h-8 items-center gap-1.5 rounded-md px-2 py-1.5 text-ui font-medium text-ink-soft transition-colors hover:bg-hover hover:text-ink"
          >
            <ChevronDown size={14} className={clsx("shrink-0 transition-transform duration-(--gx-enter)", !sheetOpen && "-rotate-90")} />
            {english ? sheet.title : tServer(sheet.title)}
          </button>
        ),
        // Fiche ouverte : le bouton ne reste jamais seul en bas de feuille, il suit le début de la fiche.
        { lang: sheetLang, keepWithNext: sheetOpen },
      );
      if (sheetPresence.mounted) {
        const exiting = sheetPresence.closing;
        addBlock(
          `es:note:${sheet.eni}`,
          16 / Math.max(u, 0.01),
          { before: 0, after: 6 },
          () => (
            <p className="text-meta text-ink-muted" style={{ paddingLeft: px(TEXT.sheet.indent * u) }}>
              {english ? sheet.note : tServer(sheet.note)}
            </p>
          ),
          { lang: sheetLang, sheet: true, exiting, keepWithNext: !exiting },
        );
        sheet.text
          .split(/\n{2,}/)
          .forEach((part, i) => addText(`es:p:${sheet.eni}:${i}`, part.trim(), "sheet", { lang: sheetLang, sheet: true, exiting }));
      }
    } else {
      const id = node.id;
      const section = sections[id];
      if (!section) continue;
      const variant = node.variant;
      const live = job?.live[id];
      const writing = Boolean(job?.running && job.steps.some((s) => s.id === id && s.state === "running"));
      const queued =
        Boolean(job?.running) &&
        !live &&
        !writing &&
        section.kind === "llm" &&
        !job?.sections[id] &&
        (section.status === "pending" || !section.text);
      const isEditing = editing === id;
      const kind = variant === "lead" ? "label" : variant === "bia" ? "bia" : "section";
      // Rubrique d'en-tête de la fiche pays : toute entière à côté de la carte ; en cours de modification (ou sur
      // une feuille étroite), sous elle, l'éditeur en pleine largeur.
      const pinned = Boolean(node.pinned) && !narrow && !isEditing;
      if (section.title || variant === "lead") {
        // Fiche pays : l'intitulé peut aller à côté d'une carte s'il tient sur une ligne de la colonne, pastilles
        // comprises (largeur mesurée ; à défaut, celle du titre).
        const titleFont = `600 ${headings[kind].size * u}px ${FONT}`;
        const minWidth = intrinsic[`h:${id}`] ?? lineWidth(section.title, titleFont);
        addBlock(
          `h:${id}`,
          headings[kind].leading,
          headings[kind],
          () => (
            <SectionHeader
              section={section}
              variant={variant}
              headings={headings}
              fit={fiche}
              u={u}
              writing={writing}
              queued={queued}
              busy={busy}
              actions={!isEditing && Boolean(section.text) && !writing}
              active={hovered === id}
              onEdit={() => onEdit(id)}
              onRegenerate={() => onRegenerate(id)}
              onReset={() => onReset(id)}
              onCopied={onCopied}
              chips={renderChips?.(section, { writing, queued, busy })}
              regenerable={canRegenerate ? canRegenerate(section) : true}
              regenerateLabel={regenerateLabel}
              rubric={rubrics}
            />
          ),
          { keepWithNext: true, section: id, beside: fiche && !narrow, minWidth, pinned },
        );
      }
      // Carte de la rubrique (commandements), juste sous son intitulé : sa liste coule à côté d'elle.
      if (node.figure) addFigure(node.figure);
      const style: TextStyleId = variant === "lead" ? "lead" : "body";
      const body = { before: 0, after: styles.body.after };
      const firstEntry = entries.length;
      if (isEditing) {
        addBlock(
          `e:${id}`,
          120,
          body,
          () => (
            <Editor
              initial={section.text}
              copyKey={`${copyPrefix}${id}`}
              saving={savingId === id}
              u={u}
              textStyle={styles[style]}
              hint={editHint?.(id)}
              rubric={rubrics}
              onSave={(text) => onSave(id, text)}
              onCancel={onCancelEdit}
            />
          ),
          { section: id },
        );
      } else if (live && live.draft && lang === "en") {
        // Premier jet français d'un dossier anglais : jamais montré, seul le texte traduit s'affiche.
        addBlock(`s:${id}`, 64, body, () => <Skeleton label={t("L'IA rédige le paragraphe, puis le traduit en anglais…")} />, {
          section: id,
        });
      } else if (live) {
        const parts = paragraphs(live.text);
        parts.forEach((p, i) =>
          addText(`p:${id}:${i}`, p, style, {
            section: id,
            caret: i === parts.length - 1,
            live: i === parts.length - 1,
            italic: live.draft,
            muted: live.draft,
          }),
        );
        if (!parts.length) addBlock(`s:${id}`, 64, body, () => <Skeleton label={t("L'IA prépare le texte…")} />, { section: id });
      } else if (writing || queued) {
        addBlock(
          `s:${id}`,
          64,
          body,
          () => <Skeleton label={writing ? t("L'IA prépare le texte…") : t("En attente de l'IA…")} />,
          { section: id },
        );
      } else if (section.translating) {
        // Lu dans l'autre langue (docs/13) : la traduction de ce paragraphe arrive, attente animée à sa place.
        addBlock(`s:${id}`, 64, body, () => <Skeleton label={t("Traduction…")} />, { section: id });
      } else if (section.text.trim() && variant === "facts" && node.columns === 2 && !narrow) {
        // Fiche pays : deux colonnes de lignes « Libellé : valeur », rangée par rangée comme le PDF ; chaque rangée
        // est un bloc (une rangée ne se coupe pas d'une page à l'autre).
        const items = textLines(section.text);
        const column = ((FRAME_WIDTH - FICHE.gutter) / 2) * u;
        for (let r = 0; r * 2 < items.length; r++) {
          const pair = items.slice(r * 2, r * 2 + 2);
          addBlock(
            `r:${id}:${r}`,
            styles.facts.leading,
            { before: 0, after: styles.facts.after },
            () => (
              <div
                className={clsx("grid", section.stale && "opacity-60")}
                style={{ gridTemplateColumns: `${px(column)} ${px(column)}`, columnGap: px(FICHE.gutter * u) }}
                onDoubleClick={busy ? undefined : () => onEdit(id)}
                title={busy ? undefined : t("Double-cliquez pour modifier")}
              >
                {pair.map((line, c) => (
                  <p key={c} style={textCss("facts", styles.facts, u, false, false)}>
                    <Rich text={line} bold={fieldLabelLength(line)} />
                  </p>
                ))}
              </div>
            ),
            { section: id },
          );
        }
      } else if (section.text.trim() && variant === "facts") {
        // Une ligne « Libellé : valeur » par entrée, libellé en gras.
        textLines(section.text).forEach((line, i) =>
          addText(`p:${id}:${i}`, line, "facts", {
            section: id,
            muted: section.stale,
            editable: !busy,
            bold: fieldLabelLength(line),
            pinned,
          }),
        );
      } else if (section.text.trim() && variant === "list") {
        listItems(section.text).forEach((item, i) =>
          addText(`p:${id}:${i}`, item, "bullet", { section: id, muted: section.stale, editable: !busy, bullet: true }),
        );
      } else if (section.text.trim()) {
        paragraphs(section.text).forEach((p, i) =>
          addText(`p:${id}:${i}`, p, style, { section: id, muted: section.stale, editable: !busy }),
        );
      } else {
        addBlock(
          `x:${id}`,
          18,
          body,
          () => (
            <p className="text-ui text-ink-muted italic">
              {/* Paragraphe retenu (force non française, il nommait un ennemi de référence) : « À régénérer » le
                  rédige de nouveau (registre B39). */}
              {rubrics
                ? section.withheld
                  ? t("Rubrique à rédiger de nouveau : elle nommait un ennemi de référence.")
                  : t("Rubrique vide.")
                : section.withheld
                  ? t("Paragraphe à rédiger de nouveau : il nommait un ennemi de référence.")
                  : t("Pas encore rédigé.")}
            </p>
          ),
          { section: id },
        );
      }
      // Fiche pays : après le dernier élément d'une rubrique (ligne de chiffres, puce), l'espace d'un paragraphe,
      // comme le PDF.
      const last = entries.length > firstEntry ? entries[entries.length - 1] : undefined;
      if (fiche && last && last.kind !== "float") last.after = styles.body.after * u;
    }
  }
  const byKey = new Map(entries.map((e) => [e.key, e]));

  // --- mesure des paragraphes (conteneur caché, à la largeur du cadre de texte) ----------------------------------
  const scratch = useRef<HTMLParagraphElement>(null);
  const cache = useRef({ scale: "", lines: new Map<string, number>() });
  const scale = `${u}|${typoKey}|${fontEpoch}`;
  // `width` : largeur de la colonne à côté d'une carte (fiche pays) ; celle du cadre par défaut.
  const countLines = (item: TextItem, from: number, to: number, width?: number): number => {
    const entry = byKey.get(item.key);
    const el = scratch.current;
    if (!entry || entry.kind !== "text" || !el) return Math.max(1, Math.ceil((to - from) / 12));
    if (cache.current.scale !== scale || cache.current.lines.size > 4000) cache.current = { scale, lines: new Map() };
    const caret = entry.caret && to >= entry.words;
    const slice = sliceWords(entry.text, entry.spans, from, to);
    const bold = boldFrom(entry, from);
    const w = width ?? FRAME_WIDTH * u;
    const key = `${Math.round(w * 4)}|${entry.style}|${entry.lang ?? lang}|${entry.italic ? 1 : 0}|${caret ? 1 : 0}|${bold}|${slice}`;
    const known = cache.current.lines.get(key);
    if (known !== undefined) return known;
    Object.assign(el.style, textCss(entry.style, styles[entry.style], u, entry.italic, false), { width: px(w) });
    // Même langue que le morceau affiché (règles de coupure des lignes).
    el.lang = entry.lang ?? lang;
    fillRich(el, slice, bold);
    if (caret) {
      const mark = document.createElement("span");
      mark.className = "briefing-caret";
      el.appendChild(mark);
    }
    const lines = Math.max(1, Math.round(el.getBoundingClientRect().height / entry.lineHeight));
    cache.current.lines.set(key, lines);
    return lines;
  };

  // --- mise en pages ------------------------------------------------------------------------------------------
  // Structure changée (bloc, hauteur, largeur, polices) : tout de suite, avant l'affichage ; texte qui s'écrit :
  // une mise en pages par image au plus.
  const [paged, setPaged] = useState<Paged | null>(null);
  // Fiche pays : règles de son PDF (espaces fusionnés, veuves) et colonnes à côté des cartes.
  const compute = () =>
    paginate(entries, FRAME_HEIGHT * u, countLines, { width: FRAME_WIDTH * u, mergeSpaces: fiche, strictWidows: fiche });
  const computeRef = useRef(compute);
  computeRef.current = compute;
  const structure =
    entries
      .map((e) =>
        e.kind === "block"
          ? `${e.key}:${Math.round(e.height * 2)}:${e.keepWithNext ? 1 : 0}:${e.beside ? Math.round(e.minWidth ?? 0) : "-"}`
          : e.kind === "float"
            ? `${e.key}:${Math.round(e.width)}x${Math.round(e.height)}`
            : `${e.key}:${e.style}`,
      )
      .join("|") + `|${scale}`;
  const content = entries.map((e) => (e.kind === "text" ? `${e.italic ? 1 : 0}${e.caret ? 1 : 0}${e.text}` : "")).join("\u0001");
  const lastStructure = useRef("");
  const frame = useRef(0);
  useLayoutEffect(() => {
    if (!u) return;
    const run = () => {
      frame.current = 0;
      setPaged(computeRef.current());
    };
    if (structure !== lastStructure.current) {
      lastStructure.current = structure;
      cancelAnimationFrame(frame.current);
      run();
    } else if (!frame.current) frame.current = requestAnimationFrame(run);
  }, [structure, content, u]);
  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  // Feuilles : format A4, écart de l'OPO ; une feuille qui n'a plus de contenu s'efface en fondu.
  const sheetHeight = PAGE.height * u;
  const gap = 40;
  const count = paged?.pages.length ?? 0;
  const pile = stack(paged?.pages ?? [], sheetHeight, PAGE.top * u, FRAME_HEIGHT * u, PAGE.bottom * u, gap);
  const [shown, setShown] = useState(count);
  if (count > shown) setShown(count);
  useEffect(() => {
    if (count >= shown) return;
    const timer = window.setTimeout(() => setShown(count), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [count, shown]);

  const frameLeft = PAGE.left * u;
  const frameWidth = FRAME_WIDTH * u;
  const pieces = (paged?.pieces ?? []).filter((p) => byKey.has(p.key) && p.page < pile.tops.length);
  const topOf = (page: number, y: number) => (pile.tops[page] ?? 0) + PAGE.top * u + y;

  // Zones de survol par section, par page et par colonne (d'un seul tenant, entre les paragraphes compris), filet de
  // la fiche.
  const zones = new Map<string, { id: string; top: number; bottom: number; left: number; width: number }>();
  const rules = new Map<number, { top: number; bottom: number; exiting: boolean }>();
  for (const p of pieces) {
    const entry = byKey.get(p.key);
    if (!entry) continue;
    const top = topOf(p.page, p.top);
    const bottom = top + p.height;
    if (entry.section && entry.section !== editing) {
      const key = `${entry.section}@${p.page}@${Math.round(p.left)}`;
      const zone = zones.get(key);
      zones.set(
        key,
        zone ? { ...zone, bottom: Math.max(zone.bottom, bottom) } : { id: entry.section, top, bottom, left: p.left, width: p.width },
      );
    }
    if (entry.sheet) {
      const rule = rules.get(p.page);
      rules.set(
        p.page,
        rule ? { ...rule, bottom: Math.max(rule.bottom, bottom) } : { top, bottom, exiting: Boolean(entry.exiting) },
      );
    }
  }
  const pad = 3 * u;

  return (
    <div className="mx-auto max-w-[860px] px-8 py-10">
      <div
        ref={stackRef}
        className="relative"
        // Comme la feuille de l'OPO : ni la largeur condensée ni l'approche de la charte (body), pour les feuilles,
        // le texte, l'éditeur et le conteneur de mesure (aussi pendant le chargement d'Inter OPO).
        style={{ height: pile.total, letterSpacing: "normal", fontStretch: "normal" }}
        onMouseOver={(e) => {
          onOver(e);
          unitOver(e);
        }}
        onMouseOut={unitOut}
        onMouseLeave={() => setHovered(null)}
      >
        {Array.from({ length: Math.max(shown, count) }, (_, i) => {
          const ghost = i >= count;
          const top = ghost ? pile.total + gap + (i - count) * (sheetHeight + gap) : (pile.tops[i] ?? 0);
          return (
            <Sheet
              key={i}
              top={top}
              height={ghost ? sheetHeight : (pile.heights[i] ?? sheetHeight)}
              u={u}
              n={i + 1}
              total={count}
              banner={banner}
              themeLine={themeLine}
              ghost={ghost}
            />
          );
        })}
        <MentionContext.Provider value={matcher}>
        <article lang={lang}>
          {[...zones.entries()].map(([key, z]) => (
            <div
              key={`z:${key}`}
              data-section={z.id}
              aria-hidden
              className={clsx(
                // Survol d'un paragraphe : accent à 6 %, comme sur l'OPO ; le paragraphe en cours d'édition est à 12 %.
                "absolute rounded-[3px] transition-colors duration-(--gx-enter)",
                hovered === z.id ? "bg-accent/[0.06]" : "bg-transparent",
              )}
              style={{ top: z.top - pad, height: z.bottom - z.top + 2 * pad, left: frameLeft + z.left - pad, width: z.width + 2 * pad }}
            />
          ))}
          {[...rules.entries()].map(([page, r]) => (
            <div
              key={`r:${page}`}
              aria-hidden
              className={clsx("absolute w-0.5 bg-line-strong", r.exiting ? motion("top", true) : "gx-enter-fade")}
              style={{ top: r.top, height: r.bottom - r.top, left: frameLeft }}
            />
          ))}
          {pieces.map((p) => {
            const entry = byKey.get(p.key) as Entry;
            // À côté d'une carte (fiche pays) : la colonne qui reste ; sinon, la largeur du cadre.
            const position: CSSProperties = { position: "absolute", top: topOf(p.page, p.top), left: frameLeft + p.left, width: p.width };
            const animation = entry.exiting ? motion("top", true) : "gx-enter-fade";
            if (entry.kind === "float") {
              return (
                <div key={p.key} className={animation} style={{ ...position, height: p.height }}>
                  {entry.render(p.width, p.height)}
                </div>
              );
            }
            if (entry.kind === "block") {
              return (
                <div key={p.key} ref={blockRef(p.key)} data-section={entry.section} lang={entry.lang} className={animation} style={position}>
                  {entry.render()}
                </div>
              );
            }
            const to = p.last ? entry.words : p.to;
            const slice = sliceWords(entry.text, entry.spans, p.from, to);
            // Paragraphe coupé : le premier fragment porte aussi la suite, pour les lecteurs d'écran seulement ;
            // les fragments suivants ne sont pas relus.
            const rest = p.first && !p.last ? sliceWords(entry.text, entry.spans, p.to, entry.words) : "";
            const section = entry.section;
            // Fragment coupé en bas de page : seule sa toute dernière ligne est justifiée, pas les lignes courtes
            // finies par un retour simple (le PDF ne les justifie pas).
            const split = p.last ? null : splitAtLastBreak(slice);
            const bold = boldFrom(entry, p.from);
            return (
              <p
                key={`${p.key}#${p.part}`}
                data-section={section}
                lang={entry.lang}
                aria-hidden={p.first ? undefined : true}
                className={clsx(animation, entry.muted && "opacity-60")}
                style={{ ...position, ...textCss(entry.style, styles[entry.style], u, entry.italic, !p.last && !split) }}
                onDoubleClick={entry.editable && section ? () => onEdit(section as Id) : undefined}
                title={entry.editable ? t("Double-cliquez pour modifier") : undefined}
              >
                {split ? (
                  <>
                    <Rich text={split[0]} />
                    <span style={{ display: "block", textAlignLast: "justify" }}>
                      <Rich text={split[1]} />
                    </span>
                  </>
                ) : (
                  <Rich text={slice} bold={bold} />
                )}
                {/* Puce d'un élément de liste (fiche pays), au premier fragment, comme dans le PDF. */}
                {entry.bullet && p.first && (
                  <span aria-hidden style={{ position: "absolute", top: 0, left: px((fiche ? FICHE.bulletAt : 3) * u) }}>
                    •
                  </span>
                )}
                {/* Hors de la sélection : copier d'une feuille à l'autre ne double pas la suite. */}
                {rest && <span className="sr-only select-none"> {rest}</span>}
                {entry.caret && p.last && <span className="briefing-caret" aria-hidden />}
              </p>
            );
          })}
        </article>
        </MentionContext.Provider>
        {peek.element}
        <div
          aria-hidden
          className="pointer-events-none invisible absolute top-0 left-0 h-0 overflow-hidden"
          style={{ width: frameWidth, contain: "layout style" }}
        >
          <p ref={scratch} />
        </div>
      </div>
    </div>
  );
}

/** Feuille A4 et cadre des pages du PDF : bandeau encadré de rouge et thème tactique en tête, « GENEREX » et
 *  « Page n/N » en pied (positions de `to_pdf`, mises à l'échelle). Décor : les lecteurs d'écran l'ignorent. */
function Sheet({
  top,
  height,
  u,
  n,
  total,
  banner,
  themeLine,
  ghost,
}: {
  top: number;
  height: number;
  u: number;
  n: number;
  total: number;
  banner: string;
  themeLine: string;
  ghost: boolean;
}) {
  const line = (size: number): CSSProperties => ({
    position: "absolute",
    left: px(PAGE.left * u),
    right: px(PAGE.right * u),
    margin: "0px",
    fontFamily: FONT,
    fontSize: px(size * u),
    fontWeight: "400",
    lineHeight: 1,
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    letterSpacing: "normal",
  });
  return (
    <div
      aria-hidden
      className={clsx("absolute inset-x-0 bg-white shadow-paper select-none", ghost ? "gx-exit-fade" : "gx-enter-fade")}
      style={{ top, height }}
    >
      {!ghost && (
        <>
          <div className="absolute inset-x-0 flex justify-center" style={{ top: px(30 * u) }}>
            <span
              style={{
                display: "flex",
                alignItems: "flex-end",
                boxSizing: "border-box",
                height: px(21 * u),
                padding: `0 ${px(8 * u)} ${px(4.75 * u)}`,
                border: `${px(0.75 * u)} solid ${RED}`,
                fontFamily: FONT,
                fontSize: px(11 * u),
                fontWeight: "600",
                lineHeight: 1,
                color: RED,
                whiteSpace: "nowrap",
                letterSpacing: "normal",
              }}
            >
              {banner}
            </span>
          </div>
          <p style={{ ...line(10), top: px((69 - BASELINE * 10) * u), color: GRAY }}>{themeLine}</p>
          <p style={{ ...line(8), bottom: px((42 - (1 - BASELINE) * 8) * u), color: "rgb(128 128 128 / 0.6)" }}>GENEREX</p>
          <p style={{ ...line(10), bottom: px((30 - (1 - BASELINE) * 10) * u), color: GRAY }}>{`Page ${n}/${total}`}</p>
        </>
      )}
    </div>
  );
}

function SectionHeader({
  section,
  variant,
  headings,
  fit = false,
  u,
  writing,
  queued,
  busy,
  actions,
  active,
  onEdit,
  onRegenerate,
  onReset,
  onCopied,
  chips,
  regenerable = true,
  regenerateLabel,
  rubric = false,
}: {
  section: PagedSection;
  variant: SectionVariant;
  /** Titres du document (ceux du niveau de la fiche pays). */
  headings: HeadingStyles;
  /** Fiche pays : jamais plus large que sa colonne (pastilles en trop masquées). */
  fit?: boolean;
  u: number;
  writing: boolean;
  queued: boolean;
  busy: boolean;
  actions: boolean;
  active: boolean;
  onEdit: () => void;
  onRegenerate: () => void;
  onReset: () => void;
  onCopied: () => void;
  /** Pastilles propres au document (fiche pays) ; absentes : celles de la mise en ambiance. */
  chips?: ReactNode;
  /** Le bouton « rédiger à nouveau » est proposé. */
  regenerable?: boolean;
  regenerateLabel?: string;
  /** Rubrique de la fiche pays : « rubrique » plutôt que « paragraphe » dans les infobulles. */
  rubric?: boolean;
}) {
  const ai = section.kind === "llm";
  const regenerate = regenerateLabel ?? (ai ? t("Rédiger à nouveau par l'IA") : t("Autre rédaction d'après la carte"));
  const editLabel = rubric ? t("Modifier cette rubrique") : t("Modifier ce paragraphe");
  const copyLabel = rubric ? t("Copier la rubrique") : t("Copier le paragraphe");
  const color = variant === "enemy" || variant === "bia" ? RED : variant === "friendly" ? BLUE : GRAY;
  const kind = variant === "lead" ? "label" : variant === "bia" ? "bia" : "section";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(section.text);
      onCopied();
    } catch {
      /* presse-papiers refusé : rien à faire */
    }
  };
  return (
    // Hauteur d'une ligne de titre du PDF : les pastilles et les actions débordent sans allonger la page. Titre et
    // pastilles dans un conteneur mesuré (`data-intrinsic`) : à côté d'une carte, l'intitulé n'y va que s'il tient
    // d'une ligne ; fiche pays (`fit`) : les pastilles en trop sont masquées plutôt que de déborder de la feuille.
    <div className="group relative flex items-center" style={{ height: px(headings[kind].leading * u) }} aria-busy={writing || queued}>
      <div data-intrinsic className={clsx("flex items-center gap-2", fit && "min-w-0 overflow-hidden")}>
        {variant === "lead" ? (
          <span
            className="tracking-[0.08em] whitespace-nowrap text-ink-muted uppercase"
            style={{ fontFamily: FONT, fontSize: px(headings.label.size * u), fontWeight: 600, lineHeight: 1 }}
          >
            {section.title}
          </span>
        ) : (
          <h5 className="whitespace-nowrap" style={headingCss(headings[kind], u, color)}>
            {section.title}
          </h5>
        )}
        {chips !== undefined ? (
          chips
        ) : (
          <SectionChips section={section} writing={writing} queued={queued} onRegenerate={onRegenerate} busy={busy} />
        )}
      </div>
      {actions && (
        <div
          className={clsx(
            "absolute top-1/2 right-0 flex -translate-y-1/2 items-center gap-0.5 rounded-md bg-white/90 transition-opacity",
            active ? "opacity-100" : "opacity-0 group-focus-within:opacity-100",
          )}
        >
          <IconButton size="sm" title={editLabel} aria-label={editLabel} onClick={onEdit} disabled={busy}>
            <Pencil size={14} />
          </IconButton>
          {regenerable && (
            <IconButton size="sm" title={regenerate} aria-label={regenerate} onClick={onRegenerate} disabled={busy}>
              <RefreshCw size={14} />
            </IconButton>
          )}
          {section.edited && (
            <IconButton size="sm" title={t("Rétablir le texte généré")} aria-label={t("Rétablir le texte généré")} onClick={onReset} disabled={busy}>
              <RotateCcw size={14} />
            </IconButton>
          )}
          <IconButton size="sm" title={copyLabel} aria-label={copyLabel} onClick={() => void copy()}>
            <Copy size={14} />
          </IconButton>
        </div>
      )}
    </div>
  );
}

function SectionChips({
  section,
  writing,
  queued,
  busy,
  onRegenerate,
}: {
  section: PagedSection;
  writing: boolean;
  queued: boolean;
  busy: boolean;
  onRegenerate: () => void;
}) {
  const chips: ReactNode[] = [];
  // Ni pastille « IA » ni pastille du texte source : demande d'Alexis du 12/10/2026 ; la rédaction en cours se
  // lit sur la carte d'attente des feuilles.
  if (section.edited) chips.push(<Badge key="edited" tone="accent">{t("Modifié")}</Badge>);
  if (!writing && !queued && section.stale) {
    chips.push(
      <button
        key="stale"
        type="button"
        data-attention={STALE_REGENERATE}
        onClick={onRegenerate}
        disabled={busy}
        title={t("Le dossier a changé depuis la rédaction (ennemi, mission, unité…) : rédiger à nouveau")}
        className="inline-flex rounded-md transition-[filter] hover:brightness-95 disabled:opacity-40"
      >
        <Badge tone="warn" className="gap-1">
          <RefreshCw size={12} /> {t("À régénérer")}
        </Badge>
      </button>,
    );
  }
  return chips.length ? <div className="flex shrink-0 items-center gap-1.5">{chips}</div> : null;
}

/** Carte de la fiche pays dans la feuille, à sa taille dans le PDF : elle apparaît en fondu une fois chargée ;
 *  illisible, un mot la remplace. */
function Figure({ src, alt, width, height }: { src: string; alt: string; width: number; height: number }) {
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  return (
    <div className="flex justify-center">
      <div className={clsx("relative overflow-hidden", state !== "ready" && "bg-canvas")} style={{ width, height }}>
        {state === "failed" ? (
          <span className="absolute inset-0 flex items-center justify-center text-meta text-ink-muted">
            {t("Carte indisponible")}
          </span>
        ) : (
          <img
            src={src}
            alt={alt}
            draggable={false}
            onLoad={() => setState("ready")}
            onError={() => setState("failed")}
            className={clsx(
              "block h-full w-full object-contain transition-opacity duration-(--gx-enter)",
              state === "ready" ? "opacity-100" : "opacity-0",
            )}
          />
        )}
        {state === "loading" && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner decorative />
          </span>
        )}
      </div>
    </div>
  );
}

function Skeleton({ label }: { label: string }) {
  return (
    <div className="space-y-2" role="status">
      <span className="flex items-center gap-1.5 text-meta font-medium text-run">
        <Sparkles size={12} /> <Waiting>{label}</Waiting>
      </span>
      {[92, 100, 96, 70].map((w, i) => (
        <div key={i} className="briefing-skeleton h-3 rounded" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

/** Éditeur d'un paragraphe, d'un seul tenant sur la page où commence sa section, à la typographie de la feuille. */
function Editor({
  initial,
  copyKey,
  saving,
  u,
  textStyle,
  hint,
  rubric = false,
  onSave,
  onCancel,
}: {
  initial: string;
  /** Copie locale de la saisie (rangée sous la licence), rétablie à l'ouverture si elle diffère du paragraphe. */
  copyKey: string;
  saving: boolean;
  u: number;
  /** Corps et interligne du paragraphe modifié (ceux du document). */
  textStyle: TextStyle;
  /** Aide de la saisie après « Échap annule » ; absente : une ligne vide sépare deux paragraphes. */
  hint?: string;
  /** Rubrique de la fiche pays (avis et registre des saisies). */
  rubric?: boolean;
  onSave: (text: string) => Promise<unknown>;
  onCancel: () => void;
}) {
  // Texte d'une session précédente non enregistré (onglet fermé, licence perdue) : rétabli avec un avis.
  const [restored] = useState(() => {
    const copy = readLocalCopy<string>(copyKey);
    return copy && typeof copy.value === "string" && copy.value.trim() !== initial.trim() ? copy : null;
  });
  const [value, setValue] = useState(restored?.value ?? initial);
  const notify = useUi((st) => st.notify);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!restored) return;
    notify({
      text: t("Saisie retrouvée"),
      detail: rubric
        ? restored.base === initial
          ? t("Votre texte non enregistré de cette rubrique est rétabli : enregistrez-le ou annulez-le.")
          : t("Votre texte non enregistré est rétabli ; la rubrique a changé depuis : relisez avant d'enregistrer.")
        : restored.base === initial
          ? t("Votre texte non enregistré de ce paragraphe est rétabli : enregistrez-le ou annulez-le.")
          : t("Votre texte non enregistré est rétabli ; le paragraphe a changé depuis : relisez avant d'enregistrer."),
      tone: "info",
    });
  }, [restored, initial, notify, rubric]);
  // Paragraphe modifié : déclaré au registre des saisies non enregistrées (audit de persistance M4), copié sur le
  // poste ; changer de vue, de paragraphe, de dossier ou de langue demande d'abord confirmation.
  const dirty = value.trim() !== initial.trim();
  useUnsavedDraft(`briefing:${copyKey}`, dirty, {
    label: rubric ? t("Rubrique de la fiche pays") : t("Paragraphe du dossier d'exercice"),
    save: value.trim() ? () => onSave(value) : undefined,
    discard: onCancel,
    reveal: () => designate(ref.current, "field"),
    copy: { key: copyKey, value, base: initial },
  });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value, u]);
  useEffect(() => {
    const el = ref.current;
    el?.focus();
    el?.setSelectionRange(el.value.length, el.value.length);
  }, []);
  // Échec : l'avis vient de la vue, le paragraphe reste ouvert avec sa saisie.
  const save = () => (value.trim() === initial.trim() ? onCancel() : void onSave(value).catch(() => undefined));
  const s = textStyle;
  return (
    <div className="space-y-2">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            save();
          }
        }}
        spellCheck
        className="block w-full resize-none rounded-lg border border-accent/50 bg-accent/[0.12] px-3 py-2 ring-4 ring-accent/10 outline-none"
        style={{ fontFamily: FONT, fontSize: px(s.size * u), lineHeight: px(s.leading * u), fontWeight: 400, color: INK }}
      />
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
        <KbdHint className="text-meta text-ink-muted">
          <Kbd>{shortcut(t("Entrée"))}</Kbd> {t("enregistre")} · <Kbd>{t("Échap")}</Kbd>{" "}
          {hint ?? t("annule · une ligne vide sépare deux paragraphes")}
        </KbdHint>
        <div className="ml-auto flex gap-1.5">
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {t("Annuler")}
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            blocked={!value.trim()}
            onBlocked={() => {
              // Paragraphe vide : le texte est l'action attendue (R12).
              designate(ref.current, "field");
              notify({
                text: rubric ? t("Écrivez la rubrique avant de l'enregistrer.") : t("Écrivez le paragraphe avant de l'enregistrer."),
                tone: "info",
              });
            }}
          >
            <Check size={14} /> {saving ? <Waiting>{t("Enregistrement…")}</Waiting> : t("Enregistrer")}
          </Button>
        </div>
      </div>
    </div>
  );
}
